import { updateOwnProfile } from "@a/profiles";
import { useAuth } from "@h/useCtx";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

const SUPPORTED = ["en", "es", "pt", "fr", "it"] as const;
type SupportedLang = (typeof SUPPORTED)[number];

export function useLanguageSync() {
  const { user, profile } = useAuth();
  const { i18n } = useTranslation();

  // ✅ evita reaplicar el idioma del perfil múltiples veces
  const initialized = useRef(false);

  /**
   * ==========================================
   * APPLY INITIAL LANGUAGE
   * ==========================================
   *
   * Prioridad:
   * 1. localStorage (usuario eligió manualmente)
   * 2. profile.preferred_lang
   * 3. navigator/fallback (lo maneja i18next)
   */
  useEffect(() => {
    if (initialized.current) return;

    // Si ya existe idioma persistido por i18next,
    // NO sobrescribirlo con el perfil.
    const localLang = localStorage.getItem("i18nextLng");

    if (localLang) {
      initialized.current = true;
      return;
    }

    if (!profile?.preferred_lang) {
      initialized.current = true;
      return;
    }

    const base = profile.preferred_lang.split("-")[0];

    if (SUPPORTED.includes(base as SupportedLang) && i18n.language !== base) {
      i18n.changeLanguage(base);
    }

    initialized.current = true;
  }, [profile?.preferred_lang, i18n]);

  /**
   * ==========================================
   * PERSIST LANGUAGE CHANGES
   * ==========================================
   */
  useEffect(() => {
    if (!user) return;

    const handler = (lang: string) => {
      const base = lang.split("-")[0];

      if (!SUPPORTED.includes(base as SupportedLang)) return;

      // Evita writes innecesarios
      if (profile?.preferred_lang === base) return;

      updateOwnProfile({
        preferred_lang: base,
      }).catch(() => {});
    };

    i18n.on("languageChanged", handler);

    return () => {
      i18n.off("languageChanged", handler);
    };
  }, [user, profile?.preferred_lang, i18n]);
}
